/**
 * Marks the document while the user is navigating by keyboard, so a focus ring can be shown for a
 * Tab and hidden for a click.
 *
 * `:focus-visible` already does this for buttons, links and selects. It does NOT do it for text
 * inputs: browsers match `:focus-visible` on a focused text field however it was focused, because
 * the field accepts keystrokes. That is the right default for a plain form, but in an editor full of
 * text fields it means every click lights up a ring — a navigation cue shown to someone who just
 * pointed at the thing they wanted.
 *
 * So: set `data-kbd` on the first Tab or arrow key, clear it on the next pointerdown. The stylesheet
 * gates the text-input ring on that attribute and leaves every other control on plain
 * `:focus-visible`, which is already pointer-immune.
 *
 * Only navigation keys count. Typing inside a field must not turn the ring on mid-sentence, which is
 * exactly what keying on "any keydown" would do.
 */
const NAV_KEYS = new Set([
  'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown',
]);

export function installKeyboardNavTracking(): void {
  const root = document.documentElement;

  window.addEventListener('keydown', e => {
    if (NAV_KEYS.has(e.key)) root.setAttribute('data-kbd', '');
  }, true);

  // pointerdown, not click: the attribute has to be gone BEFORE focus lands, or the ring flashes for
  // a frame on the way in.
  window.addEventListener('pointerdown', () => {
    root.removeAttribute('data-kbd');
  }, true);
}
